// JAISIU-3082: 3 discrete Inspector panel width options.
// Index 0 = Compact, 1 = Default, 2 = Wide. The host persists the
// selected index via storage.inspectorWidth.
//
// Operator tweak 2026-09-03 (msg 2eff7eff): Default bumped 380 → 460 px
// and Wide bumped 520 → 600 px so the chat panel doesn't shrink when
// the operator picks a wider Inspector.
import { LitElement, html, nothing } from "lit";

export const INSPECTOR_WIDTH_PRESETS = [
  { id: "compact", label: "Compact", pixels: 280 },
  { id: "default", label: "Default", pixels: 460 },
  { id: "wide", label: "Wide", pixels: 600 },
] as const;

export type FleetInspectorWidthPickerProps = {
  /** Currently selected inspector width index (0..2). */
  selectedIndex: number;
  /** Called with (index, pixel-width) when the operator picks a preset. */
  onSelect: (index: number, pixels: number) => void;
  /**
   * JAISIU-3082 (operator tweak msg 2eff7eff): the 3 options are now
   * hidden behind a single "Size" trigger button (popover menu). When
   * false, falls back to the original inline 3-button segmented control.
   */
  asMenu?: boolean;
};

/**
 * Single trigger button that opens a small menu of the 3 width
 * options. Modeled on `<session-share>` so existing CSS variables and
 * theme work without further changes.
 */
export class FleetInspectorSizeButton extends LitElement {
  // Public properties (Lit will observe declared names).
  static properties = {
    selectedIndex: { type: Number, attribute: "selected-index" },
    onSelect: { attribute: false },
    open: { state: true },
  };

  declare selectedIndex: number;
  declare open: boolean;
  // Callback bound by host via .onSelect=. Functions are kept on the
  // instance and not reflected to attributes.
  onSelect: ((index: number, pixels: number) => void) = () => {};

  createRenderRoot(): this {
    // Render into the light DOM so the existing page CSS for
    // .fleet-inspector-width-picker applies without shadow DOM scoping.
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this._onDocClick);
    document.addEventListener("keydown", this._onKey);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKey);
  }

  private _onDocClick = (event: MouseEvent) => {
    if (!this.open) return;
    const target = event.target as Node | null;
    if (target && this.contains(target)) return;
    this.open = false;
  };

  private _onKey = (event: KeyboardEvent) => {
    if (!this.open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.open = false;
    }
  };

  private _toggle = (event: MouseEvent) => {
    event.stopPropagation();
    this.open = !this.open;
  };

  private _select(i: number, pixels: number) {
    this.open = false;
    this.onSelect(i, pixels);
  }

  render() {
    const idx = Number.isInteger(this.selectedIndex)
      ? Math.max(0, Math.min(INSPECTOR_WIDTH_PRESETS.length - 1, this.selectedIndex | 0))
      : 1;
    const current = INSPECTOR_WIDTH_PRESETS[idx];

    return html`
      <div
        class="fleet-inspector-width-picker ${this.open ? "is-open" : ""}"
        role="group"
        aria-label="Inspector panel width"
      >
        <button
          type="button"
          class="fleet-inspector-width-picker__trigger"
          aria-haspopup="menu"
          aria-expanded=${this.open ? "true" : "false"}
          title=${`Inspector width: ${current.label} (${current.pixels}px). Click to change.`}
          data-current-id=${current.id}
          @click=${this._toggle}
        >
          <span class="fleet-inspector-width-picker__trigger-label">Size</span>
          <span class="fleet-inspector-width-picker__trigger-current">${current.label}</span>
          <svg class="fleet-inspector-width-picker__chevron" viewBox="0 0 10 6" aria-hidden="true">
            <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        ${
          this.open
            ? html`
              <div class="fleet-inspector-width-picker__menu" role="menu" aria-label="Inspector width options">
                ${INSPECTOR_WIDTH_PRESETS.map((opt, i) => html`
                  <button
                    type="button"
                    class="fleet-inspector-width-picker__opt"
                    role="menuitemradio"
                    aria-checked=${i === idx ? "true" : "false"}
                    data-index=${i}
                    data-id=${opt.id}
                    title=${`${opt.label} (${opt.pixels}px)`}
                    @click=${() => this._select(i, opt.pixels)}
                  >
                    <span class="fleet-inspector-width-picker__opt-label">${opt.label}</span>
                    <span class="fleet-inspector-width-picker__opt-pixels">${opt.pixels}px</span>
                  </button>
                `)}
              </div>
            `
            : nothing
        }
      </div>
    `;
  }
}

if (!customElements.get("fleet-inspector-size-button")) {
  customElements.define("fleet-inspector-size-button", FleetInspectorSizeButton);
}

/**
 * Renders the picker/Size button. By default uses the Size-button
 * popover (operator tweak msg 2eff7eff). Pass `asMenu: false` to fall
 * back to the original inline 3-button segmented control (kept for
 * tests and edge cases).
 */
export function renderInspectorWidthPicker(
  props: FleetInspectorWidthPickerProps,
) {
  if (props.asMenu === false) {
    const idx = Math.max(
      0,
      Math.min(INSPECTOR_WIDTH_PRESETS.length - 1, props.selectedIndex),
    );
    return html`
      <div
        class="fleet-inspector-width-picker"
        role="group"
        aria-label="Inspector panel width"
      >
        ${INSPECTOR_WIDTH_PRESETS.map(
          (opt, i) => html`
            <button
              type="button"
              class="fleet-inspector-width-picker__opt"
              data-index=${i}
              data-id=${opt.id}
              aria-pressed=${i === idx ? "true" : "false"}
              title=${`${opt.label} (${opt.pixels}px)`}
              @click=${() => props.onSelect(i, opt.pixels)}
            >
              ${opt.label}
            </button>
          `,
        )}
      </div>
    `;
  }

  // Default: the Size button popover.
  return html`
    <fleet-inspector-size-button
      .selectedIndex=${props.selectedIndex}
      .onSelect=${props.onSelect}
    ></fleet-inspector-size-button>
  `;
}
